import { useState, useEffect } from 'react';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import { Calendar, X } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import VehicleImage from './VehicleImage';
import DriverInfo from './DriverInfo';
import { TrajectoryTimeline } from './TrajectoryTimeline';
import { TripsListCompact } from './TripsListCompact';
import { GpsDevice } from '@/hooks/use-gps-devices';
import { useGpsTrips, useGpsTripReplay } from '@/hooks/use-gps-trips';
import { cn } from '@/lib/utils';

interface RouteDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId: string;
  device: GpsDevice;
  companyId: string;
  onPolylineUpdate?: (positions: Array<{ lat: number; lng: number }>) => void;
  onColoredPolylineUpdate?: (points: Array<{
    lat: number;
    lng: number;
    speed_kmh: number | null;
    fix_time: string;
  }>) => void;
  onStartReplay?: (positions: Array<{
    id: string;
    fix_time: string;
    lat: number;
    lng: number;
    speed_kmh: number | null;
    course_deg: number | null;
    ignition: boolean | null;
  }>) => void;
}

export function RouteDetailsSheet({
  open,
  onOpenChange,
  deviceId,
  device,
  companyId,
  onPolylineUpdate,
  onColoredPolylineUpdate,
  onStartReplay,
}: RouteDetailsSheetProps) {
  const [selectedTab, setSelectedTab] = useState<'trajectory' | 'trips'>('trajectory');
  const [dateRange, setDateRange] = useState({
    start: startOfDay(subDays(new Date(), 7)).toISOString(),
    end: endOfDay(new Date()).toISOString(),
  });
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);

  // Buscar viagens do dispositivo no período selecionado
  const { data: trips = [] } = useGpsTrips(companyId, {
    deviceId,
    startDate: dateRange.start,
    endDate: dateRange.end,
  });

  // Buscar posições detalhadas da viagem selecionada
  const { data: tripPositions = [] } = useGpsTripReplay(selectedTripId || '');

  // Quando trip positions mudam, atualizar polyline no mapa
  useEffect(() => {
    if (tripPositions.length > 0) {
      // Callback simples (manter compatibilidade)
      if (onPolylineUpdate) {
        onPolylineUpdate(tripPositions.map((p) => ({ lat: p.lat, lng: p.lng })));
      }
      
      // Novo callback com dados completos para trajeto colorido
      if (onColoredPolylineUpdate) {
        onColoredPolylineUpdate(tripPositions.map(p => ({
          lat: p.lat,
          lng: p.lng,
          speed_kmh: p.speed_kmh,
          fix_time: p.fix_time,
        })));
      }
    }
  }, [tripPositions, onPolylineUpdate, onColoredPolylineUpdate]);

  // Selecionar primeira viagem automaticamente quando carregar
  useEffect(() => {
    if (trips.length > 0 && !selectedTripId) {
      setSelectedTripId(trips[0].id);
    }
  }, [trips, selectedTripId]);

  return (
    <div
      className={cn(
        "fixed top-0 h-screen w-[420px] bg-background border-r shadow-2xl z-30",
        "transition-all duration-300 ease-in-out flex flex-col",
        open ? "translate-x-0" : "-translate-x-full"
      )}
      style={{
        left: 'var(--sidebar-width, 0px)'
      }}
    >
      {/* HEADER FIXO */}
      <div className="p-4 border-b bg-background">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Detalhes do Roteiro</h2>
          <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

        {/* VEHICLE INFO CARD */}
        <div className="p-4 border-b bg-muted/20">
          <div className="flex items-center gap-3 bg-background p-3 rounded-lg border">
            <VehicleImage make={device.vehicle?.make} model={device.vehicle?.model} />
            <div className="flex-1">
              <p className="font-semibold text-sm">
                {device.vehicle?.model || device.name}
              </p>
              <p className="text-xs text-muted-foreground font-mono">
                {device.vehicle?.plate}
              </p>
              <Badge variant="outline" className="mt-1 text-xs">
                {device.speed_kmh || 0} km/h
              </Badge>
            </div>
          </div>
        </div>

        {/* DRIVER INFO */}
        <div className="px-4 py-3 border-b bg-background">
          <DriverInfo driver={device.driver} />
        </div>

        {/* DATE RANGE PICKER */}
        <div className="p-4 border-b space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Calendar className="h-4 w-4 text-primary" />
            <span>Período</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Início</label>
              <DatePicker
                value={dateRange.start}
                onChange={(date) =>
                  setDateRange((prev) => ({
                    ...prev,
                    start: startOfDay(new Date(date || new Date())).toISOString(),
                  }))
                }
                className="w-full text-xs"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Fim</label>
              <DatePicker
                value={dateRange.end}
                onChange={(date) =>
                  setDateRange((prev) => ({
                    ...prev,
                    end: endOfDay(new Date(date || new Date())).toISOString(),
                  }))
                }
                className="w-full text-xs"
              />
            </div>
          </div>
        </div>

        {/* TABS */}
        <Tabs
          value={selectedTab}
          onValueChange={(v) => setSelectedTab(v as any)}
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsList className="w-full justify-start px-4 rounded-none border-b">
            <TabsTrigger value="trajectory" className="flex-1">
              Trajeto
            </TabsTrigger>
            <TabsTrigger value="trips" className="flex-1">
              Viagens
            </TabsTrigger>
          </TabsList>

          {/* ABA TRAJETO - Timeline de Eventos */}
          <TabsContent value="trajectory" className="flex-1 overflow-hidden m-0">
            <TrajectoryTimeline
              trips={trips}
              tripPositions={tripPositions}
              onTripSelect={setSelectedTripId}
              selectedTripId={selectedTripId}
              companyId={companyId}
              deviceId={deviceId}
              dateRange={dateRange}
            />
          </TabsContent>

          {/* ABA VIAGENS - Lista de Viagens com Replay */}
          <TabsContent value="trips" className="flex-1 overflow-hidden m-0">
            <TripsListCompact
              trips={trips}
              onTripSelect={setSelectedTripId}
              selectedTripId={selectedTripId}
              tripPositions={tripPositions}
              onStartReplay={onStartReplay}
              onViewTrajectory={() => {}}
            />
          </TabsContent>
        </Tabs>
    </div>
  );
}
